import React, { createContext, useContext, useEffect, useState } from "react";
import TopUpCreditsDialog from "@/taskpane/components/TopUpCreditsDialog";
import { topUpEvents, TOP_UP_REQUIRED } from "@/utils/events";

import { useSubscription } from "@/hooks/useSubscription";

interface TopUpDialogContextType {
    openTopUpDialog: () => void;
    closeTopUpDialog: () => void;
}

const TopUpDialogContext = createContext<TopUpDialogContextType | undefined>(undefined);

export const TopUpDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { data: subscription } = useSubscription();

    useEffect(() => {
        const handleTopUpRequired = () => {
            setIsOpen(true);
        };

        topUpEvents.on(TOP_UP_REQUIRED, handleTopUpRequired);

        return () => {
            topUpEvents.off(TOP_UP_REQUIRED, handleTopUpRequired);
        };
    }, []);

    useEffect(() => {
        if (subscription && subscription.id !== "placeholder" && (subscription.total_credits || 0) <= 0) {
            setIsOpen(true);
        }
    }, [subscription]);

    const openTopUpDialog = () => setIsOpen(true);
    const closeTopUpDialog = () => setIsOpen(false);

    return (
        <TopUpDialogContext.Provider value={{ openTopUpDialog, closeTopUpDialog }}>
            {children}
            <TopUpCreditsDialog
                open={isOpen}
                onOpenChange={setIsOpen}
            />
        </TopUpDialogContext.Provider>
    );
};

export const useTopUpDialog = () => {
    const context = useContext(TopUpDialogContext);
    if (context === undefined) {
        throw new Error("useTopUpDialog must be used within a TopUpDialogProvider");
    }
    return context;
};
